<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();
const route = useRoute();

const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);
const botList = computed(() => getters['agentBots/getBots'].value);

const filters = reactive({
  conversationId: '',
  agentId: '',
  botId: '',
  startDate: '',
  endDate: '',
});

const routerPage = computed(() => Number(route.query.page ?? 1));

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', {
      page,
      conversationId: filters.conversationId || undefined,
      agentId: filters.agentId || undefined,
      botId: filters.botId || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page } });
};

const applyFilters = () => {
  router.push({ name: 'auditlogs_list', query: { page: 1 } });
  fetchAuditLogs(1);
};

const clearFilters = () => {
  filters.conversationId = '';
  filters.agentId = '';
  filters.botId = '';
  filters.startDate = '';
  filters.endDate = '';
  applyFilters();
};

const hasActiveFilters = computed(() =>
  filters.conversationId ||
  filters.agentId ||
  filters.botId ||
  filters.startDate ||
  filters.endDate
);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('agentBots/fetch');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});

const tableHeaders = computed(() => [
  t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
]);

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value =>
    Array.isArray(value) ? value.join(', ') : value;

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('AUDIT_LOGS.HEADER')"
      :description="$t('AUDIT_LOGS.DESCRIPTION')"
      :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
      feature-name="audit_logs"
    />

    <!-- Filter bar -->
    <div class="mt-4 flex flex-wrap gap-3 items-end">
      <div class="flex flex-col gap-1 min-w-[9rem]">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('AUDIT_LOGS.FILTERS.CONVERSATION_ID') }}
        </label>
        <input
          v-model="filters.conversationId"
          type="text"
          class="rounded border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-woot-500"
          :placeholder="$t('AUDIT_LOGS.FILTERS.CONVERSATION_ID_PLACEHOLDER')"
          @keyup.enter="applyFilters"
        />
      </div>

      <div class="flex flex-col gap-1 min-w-[9rem]">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('AUDIT_LOGS.FILTERS.AGENT') }}
        </label>
        <select
          v-model="filters.agentId"
          class="rounded border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
        >
          <option value="">{{ $t('AUDIT_LOGS.FILTERS.ALL') }}</option>
          <option
            v-for="agent in agentList"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1 min-w-[9rem]">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('AUDIT_LOGS.FILTERS.BOT') }}
        </label>
        <select
          v-model="filters.botId"
          class="rounded border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
        >
          <option value="">{{ $t('AUDIT_LOGS.FILTERS.ALL') }}</option>
          <option
            v-for="bot in botList"
            :key="bot.id"
            :value="bot.id"
          >
            {{ bot.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1 min-w-[8rem]">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('AUDIT_LOGS.FILTERS.START_DATE') }}
        </label>
        <input
          v-model="filters.startDate"
          type="date"
          class="rounded border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
        />
      </div>

      <div class="flex flex-col gap-1 min-w-[8rem]">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('AUDIT_LOGS.FILTERS.END_DATE') }}
        </label>
        <input
          v-model="filters.endDate"
          type="date"
          class="rounded border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
        />
      </div>

      <div class="flex gap-2">
        <button
          class="rounded bg-woot-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-woot-600 focus:outline-none"
          @click="applyFilters"
        >
          {{ $t('AUDIT_LOGS.FILTERS.APPLY') }}
        </button>
        <button
          v-if="hasActiveFilters"
          class="rounded border border-n-weak px-4 py-1.5 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 focus:outline-none"
          @click="clearFilters"
        >
          {{ $t('AUDIT_LOGS.FILTERS.CLEAR') }}
        </button>
      </div>
    </div>

    <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('AUDIT_LOGS.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('AUDIT_LOGS.LIST.404') }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="divide-y divide-n-weak">
          <thead>
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="auditLogItem in records" :key="auditLogItem.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4 break-all whitespace-nowrap">
                {{ generateLogText(auditLogItem) }}
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4 break-all whitespace-nowrap">
                {{
                  messageTimestamp(
                    auditLogItem.created_at,
                    'MMM dd, yyyy hh:mm a'
                  )
                }}
              </td>
              <td class="py-4 w-[8.75rem]">
                {{ auditLogItem.remote_address }}
              </td>
            </tr>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-n-weak border-t !px-0 py-4"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>
